import { apiHelpers } from '../utils/api';
import {
  Payment,
  CreatePaymentData,
  UpdatePaymentData,
  PaymentsResponse,
  PaymentFilters,
  UnpaidInvoice,
  CreateCustomerPaymentData
} from '../types/payment';

export const paymentService = {
  async getPayments(filters: PaymentFilters = {}): Promise<PaymentsResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.customerId) params.append('customerId', filters.customerId);
    if (filters.page) params.append('page', String(filters.page));
    if (filters.limit) params.append('limit', String(filters.limit));

    return await apiHelpers.get(`/payments?${params.toString()}`);
  },

  async getPayment(id: string): Promise<Payment> {
    return await apiHelpers.get(`/payments/${id}`);
  },

  async createPayment(data: CreatePaymentData): Promise<Payment> {
    return await apiHelpers.post('/payments', data);
  },

  async updatePayment(id: string, data: UpdatePaymentData): Promise<Payment> {
    return await apiHelpers.put(`/payments/${id}`, data);
  },

  async deletePayment(id: string): Promise<void> {
    return await apiHelpers.delete(`/payments/${id}`);
  },

  async getCustomerUnpaidInvoices(customerId: string): Promise<UnpaidInvoice[]> {
    return await apiHelpers.get(`/payments/customer/${customerId}/unpaid-invoices`);
  },

  async getVendorUnpaidBills(vendorId: string): Promise<any[]> {
    return await apiHelpers.get(`/payments/vendor/${vendorId}/unpaid-bills`);
  },

  async getVendorAllBills(vendorId: string): Promise<any[]> {
    return await apiHelpers.get(`/payments/vendor/${vendorId}/all-bills`);
  },

  async createCustomerPayment(data: CreateCustomerPaymentData): Promise<any> {
    return await apiHelpers.post('/payments/customer-payment', data);
  },

  // Helper function to format payment method display
  formatPaymentMethod(method: string): string {
    const methodMap: Record<string, string> = {
      'cash': 'Cash',
      'bank_transfer': 'Bank Transfer',
      'credit_card': 'Credit Card',
      'debit_card': 'Debit Card',
      'check': 'Check',
      'paypal': 'PayPal',
      'stripe': 'Stripe',
      'other': 'Other'
    };
    return methodMap[method] || method;
  },

  // Helper function to validate payment data
  validatePaymentData(data: CreatePaymentData): string[] {
    const errors: string[] = [];

    if (!data.amount || data.amount <= 0) {
      errors.push('Payment amount must be greater than 0');
    }

    if (!data.customerId && !data.vendorId) {
      errors.push('Either customer or vendor must be specified');
    }

    return errors;
  },

  // Helper function to calculate total allocated amount
  calculateTotalAllocated(allocations: Array<{ amount: number }>): number {
    return allocations.reduce((total, allocation) => total + allocation.amount, 0);
  }
};